// TODUE V2 — M0 local-first data layer (SQLite SSOT).
// SSOT prepis: Dexie/IndexedDB → SQLite. `db` je teraz per-store proxy nad
// SQLite adapterom (storage/adapter.js) ktorý drží 3 drivery za jedným modelom:
//   • web/desktop → wa-sqlite (WASM/OPFS)
//   • mobil       → @capacitor-community/sqlite (natívny)
//   • testy/node  → better-sqlite3 (:memory:)
//
// Dexie je ZRUŠENÝ ako SSOT. Čistá pôda — žiadna migrácia existujúcich dát.
// Kernel invarianty (schema_version, obligation_pack_version, soft-delete deleted_at,
// LWW polia rev/updated_at/created_at, atomický invoice counter) ostávajú 1:1.
//
// Zdroj pravdy: MasterProject/workflows/todue-v2-mythos/DATA-MODEL-M0.md
//             + MasterProject/workflows/todue-v2-mythos/SQLITE-MIGRATION-SPEC.md
import { getEngine, currentEngine } from './storage/engine';
import { setHlcNode } from './sync/hlcClock';
import { applySchema, SCHEMA_VERSION } from './storage/schema';
import { createDb } from './storage/adapter';
import { getActiveWorkspaceId, dbNameForWorkspace } from './workspace';

// ─── Seed dáta (re-export pre testy + driveSync) ─────────────────────────────
// Zdroj pravdy seedu je storage/schema.js (applySchema ho aplikuje). Tu ich len
// re-exportujeme, lebo viaceré testy importujú `SEED_CATEGORIES`/`SEED_CATEGORY_RULES`
// z `../db` (stabilný verejný bod).
export { SEED_CATEGORIES, SEED_CATEGORY_RULES } from './storage/schema';

// ─── init guard (lazy open) ──────────────────────────────────────────────────
// `db` proxy potrebuje vedieť či je engine otvorený (backup.isOpen / initV2).
// Engine je lazy singleton v storage/engine.js — DB sa otvorí až v initV2()
// (alebo prvom write keď ensureOpen.open() prebehne). NA IMPORT-TIME sa NEspustí
// žiadny driver (jsdom by spadol na WASM); v teste je platforma 'test' → better-sqlite3.
let _initialized = false;
// Single-flight guard: súbežné initV2() volania (SettingsV2 volá getVatPayer +
// getIsSzco + listPending naraz) MUSIA čakať na to isté otvorenie. Pôvodne sa
// `_initialized = true` nastavil PRED `await getEngine()`, takže 2. súbežné
// volanie videlo true a vrátilo OKAMŽITE, kým engine ešte nebol otvorený →
// volajúci padol na „engine nie je otvorený" (app2-test 2026-07-19, deep-link
// na /v2/nastavenia). Držíme prebiehajúci promise a vraciame ho všetkým.
let _initPromise = null;

// Cross-tab race guard (fail-closed): workspace id POD KTORÝM sa engine v TEJTO
// module inštancii (= tomto tabe) reálne otvoril. `getActiveWorkspaceId()` číta
// localStorage ŽIVO pri každom volaní (zdieľané medzi tabmi), ale engine sa otvára
// PRESNE RAZ pri štarte a nemení sa kým appka beží — ak iný tab medzičasom prepne
// aktívnu firmu (setActiveWorkspaceId + reload LEN seba), tento tab má stále starý
// engine, no `getActiveWorkspaceId()` by vrátil NOVÚ firmu. driveSync (pushRow/
// pullAllStore) skladá Drive prefix zo živého `getActiveWorkspaceId()` — bez tohto
// guardu by tak pushol/pullol dáta pod prefixom firmy Y, hoci lokálna DB je firmy X
// (krížový leak medzi firmami). `undefined` = engine v tomto tabe ešte nebol otvorený
// (test s mockovaným db.js, alebo appka pred initV2) — volajúci to musí rozlíšiť od
// legitímnej predvolenej firmy (`null`).
let _boundWorkspaceId; // úmyselne bez inicializátora → undefined kým initV2 nedobehne

/**
 * Vráti workspace id, POD KTORÝM je engine v tomto module (tabe) reálne otvorený.
 * `undefined` = engine ešte nebol otvorený (initV2 nedobehol) — volajúci (driveSync
 * guard) toto MUSÍ interpretovať ako „nie je čo porovnávať", nie ako zhodu/nezhodu.
 * @returns {string|null|undefined}
 */
export function getBoundWorkspaceId() {
  return _boundWorkspaceId;
}
const ensureOpen = {
  get isOpen() {
    return !!currentEngine();
  },
  async open() {
    return initV2();
  },
};

// ─── db proxy (store API nad SQLite adapterom) ───────────────────────────────
// `getEngine()` je async lazy singleton, ale adapter potrebuje synchrónny handle
// (getExec()). Riešenie: getEngineSync() vráti už-otvorený engine. Volá sa až po
// initV2()/open() → vždy otvorený keď sa robí write (consumeri await initV2 v App
// bootstrape, testy volajú applySchema na vlastnom engine).
function getEngineSync() {
  const e = currentEngine();
  if (!e) {
    throw new Error('[v2/db] engine nie je otvorený — zavolaj initV2() pred prácou s db');
  }
  return e;
}

export const db = createDb(getEngineSync, ensureOpen);
db.verno = SCHEMA_VERSION;

// Jednorazová inicializácia pri štarte appky.
// - otvorí engine (lazy, podľa platformy) → spustí DDL + seed (applySchema)
// - best-effort navigator.storage.persist() (AC1, len web)
export function initV2() {
  if (_initialized) return Promise.resolve();
  // Prebiehajúce otvorenie → vráť ten istý promise (single-flight). `_initialized`
  // sa nastaví AŽ po úspešnom open, nie pred await — inak by súbežný volajúci videl
  // true a použil ešte-neotvorený engine.
  if (_initPromise) return _initPromise;
  _initPromise = (async () => {
    try {
      // Multi-firma (DECISIONS.md 2026-08-05): engine sa otvára pod dbName AKTÍVNEJ
      // firmy — izolácia konštrukciou (iná fyzická DB), nie query filtrom. Predvolená
      // firma (activeWorkspaceId === null) dostane presne legacy 'todue_v2' → nulová
      // migrácia pre existujúcich userov.
      const activeWorkspaceId = getActiveWorkspaceId();
      const engine = await getEngine({ dbName: dbNameForWorkspace(activeWorkspaceId) });
      await applySchema(engine);
      // Zachyť workspace id POD KTORÝM sa engine reálne otvoril — driveSync guard
      // (cross-tab race) ho porovnáva proti ŽIVÉMU getActiveWorkspaceId() pri každom
      // sync volaní. Zapisuje sa TU (nie až po celom bloku nižšie) — od tohto momentu
      // je engine v tomto tabe nezvratne naviazaný na `activeWorkspaceId`.
      _boundWorkspaceId = activeWorkspaceId;

      // ── One-time migrácia: stará IndexedDB → OPFS ───────────────────────────────
      // Scenár: user mal broken build kde engine ticho prepol z OPFS na IDB →
      // faktúry uviazli v IDB. Teraz (fixnutý build) engine deterministicky vyberie
      // OPFS → potrebujeme preniesť stranded dáta. Idempotentná: ak IDB neexistuje,
      // no-op. Try/catch: ak migrácia zlyhá, appka štartuje (žiadny hard fail).
      // LEN predvolená firma — nová firma nikdy nemala Dexie/IDB dáta pod svojím
      // vlastným dbName, migrácia by tam bola no-op aj bez guardu, ale explicitný
      // guard robí zámer čitateľným a vylučuje akýkoľvek budúci false-positive match.
      try {
        if (activeWorkspaceId === null) await migrateIdbToOpfs(engine);
      } catch (e) {
        console.warn('[v2/migrate] IDB→OPFS migrácia zlyhala, pokračujem bez nej', e);
      }

      _initialized = true; // engine JE otvorený → až teraz smú súbežní čitatelia

      // HLC clock node = per-device sync id → mint per-field `_hlc` v records mutátoroch odteraz
      // razí logický čas (Fáza 2 vlna 2). Dynamic import getSyncDeviceId obíde cyklus db↔driveSync.
      // Zlyhanie nie je fatálne: bez node mutátor `_hlc` nevyrazí → merge (commit 4) padne na LWW.
      try {
        const { getSyncDeviceId } = await import('./driveSync');
        setHlcNode(getSyncDeviceId());
      } catch (e) {
        console.warn('[v2] HLC node init zlyhal — sync padne na LWW', e);
      }
    } catch (e) {
      console.error('[v2] SQLite open/schema failed', e);
      _initPromise = null; // dovoľ retry (nový pokus otvorí nové otvorenie)
      throw e;
    }
    try {
      if (typeof navigator !== 'undefined' && navigator?.storage?.persist) {
        const granted = await navigator.storage.persist();
        if (!granted) console.warn('[v2] navigator.storage.persist() odmietnutý (best-effort)');
      }
    } catch (e) {
      console.warn('[v2] navigator.storage.persist() error', e);
    }
  })();
  return _initPromise;
}

// ─── settings zápis s LWW stampom (F1) ───────────────────────────────────────
// settings store nemá rev/updated_at z definície → bez stampu by mergeRecord
// (driveSync) padol na "keep local" a nastavenia by sa cez zariadenia nikdy
// nezosynchronizovali. Tento helper je JEDINÁ správna cesta zápisu settings —
// vždy doplní `updated_at` aby LWW merge fungoval.
//
// `table` param (Direction A, P1-ADAPTER-TX-MUTEX): default `db.settings` je GLOBÁLNY
// store → beží ako vlastná top-level tx (serializovaný cez engine mutex). VNÚTRI
// db.transaction(cb) MUSÍŠ podať `tx.settings` (napr. createInvoice counter →
// `putSetting(row, tx.settings)`), inak globálny zápis čaká na mutex, ktorý drží
// tá tx → self-deadlock (engine watchdog to nahlas zdiagnostikuje). Default NEsmeruje
// na tx connection — každý globálny zápis je samostatná transakcia.
export function putSetting(row, table = db.settings) {
  return table.put({ ...row, updated_at: new Date().toISOString() });
}

// ─── Spoločné polia helper ───────────────────────────────────────────────────
export function newCommonFields() {
  const now = new Date().toISOString();
  return {
    id: crypto.randomUUID(),
    created_at: now,
    updated_at: now,
    rev: 1,
    deleted_at: null,
    // Nezvratné kernel markery (KERNEL-CONTRACT §1). schema_version = verzia tvaru
    // dát; obligation_pack_version = verzia compliance pravidiel ktorými záznam prešiel
    // (0 = žiadne). Na každej entite (record/counterparty/container) od kernelu ďalej.
    schema_version: 1,
    obligation_pack_version: 0,
  };
}

// ─── One-time migrácia: IndexedDB → OPFS ─────────────────────────────────────
// Scenár: broken build kde engine ticho prepol z OPFS na IDB → faktúry uviazli
// v IDB. Táto funkcia sa spustí po initV2 ak engine je OPFS a existuje stará IDB.
// Idempotentná: ak IDB neexistuje alebo je prázdna, no-op. Try/catch v caller.
//
// Dexie/IDB schéma (V1): db.todue_v2 → stores: records, categories, settings, ...
// SQLite OPFS schéma (V2): rovnaké tabuľky, ale cez SQLite. Migrácia prenáša len
// 'records' (faktúry) — ostatné store data sú číselníky/seed ktoré sa zregenerujú.
//
// DÔLEŽITÉ: migrácia beží cez engine.transaction (BEGIN..COMMIT) → atomická.
// Ak zlyhá, rollback → žiadne čiastočné dáta.
async function migrateIdbToOpfs(engine) {
  // Len OPFS engine môže mať stranded IDB dáta (IDB engine = už je v IDB, žiadna migrácia).
  if (engine?.platform !== 'web:opfs') return;
  // Len browser (nie Node test).
  if (typeof window === 'undefined' || !window.indexedDB) return;

  const dbName = 'todue_v2';
  const storeName = 'records';

  // 1. Otvor starú Dexie/IDB databázu
  let idb;
  try {
    idb = await new Promise((resolve, reject) => {
      const req = window.indexedDB.open(dbName);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(new Error('IDB open zlyhal'));
    });
  } catch {
    return; // IDB neexistuje = no-op
  }

  // 2. Extrahuj recordy z IDB
  let rows = [];
  try {
    const tx = idb.transaction(storeName, 'readonly');
    const store = tx.objectStore(storeName);
    rows = await new Promise((resolve, reject) => {
      const req = store.getAll();
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(new Error('IDB getAll zlyhal'));
    });
  } catch {
    idb.close?.();
    return; // store neexistuje alebo prázdny = no-op
  }

  if (rows.length === 0) {
    idb.close?.();
    return; // žiadne dáta = no-op
  }

  // 3. Skontroluj či už existujú v OPFS (idempotentná — neprepisuj existujúce)
  const existing = await engine.all(`SELECT id FROM ${storeName} WHERE id IN (${rows.map(() => '?').join(',')})`, rows.map((r) => r.id));
  const existingIds = new Set((existing || []).map((r) => r.id));
  const toMigrate = rows.filter((r) => !existingIds.has(r.id));

  if (toMigrate.length === 0) {
    idb.close?.();
    return; // všetky už v OPFS = no-op
  }

  // 4. Bulk insert do OPFS (atomický cez transaction)
  //    Použijeme adapter serialize/deserialize aby sa JSON polia správne spracovali.
  const { serializeRow } = await import('./storage/adapter');
  await engine.transaction(async (txEngine) => {
    for (const row of toMigrate) {
      const ser = serializeRow(storeName, row);
      const cols = Object.keys(ser);
      const placeholders = cols.map(() => '?').join(',');
      const sql = `INSERT INTO ${storeName} (${cols.join(',')}) VALUES (${placeholders})`;
      await txEngine.run(sql, cols.map((c) => ser[c]));
    }
  });

  // 5. Zmaž starú IDB (best-effort — ak zlyhá, nie je to fatálne)
  try {
    idb.close?.();
    await new Promise((resolve) => {
      const req = window.indexedDB.deleteDatabase(dbName);
      req.onsuccess = () => resolve(true);
      req.onerror = () => resolve(false);
    });
    console.warn(`[v2/migrate] IDB→OPFS migrácia OK: ${toMigrate.length} záznamov prenesených, stará IDB zmazaná.`);
  } catch (e) {
    console.warn('[v2/migrate] IDB zmazanie zlyhalo (dáta už v OPFS, OK)', e);
  }
}
